import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import gameWeeksUrl from '#src/components/card/Overlay/Stats/assets/stats/game_weeks.svg';
import gameWeeksMLBUrl from '#src/components/card/Overlay/Stats/assets/stats/game_weeks_mlb.svg';
import gameWeeksNBAUrl from '#src/components/card/Overlay/Stats/assets/stats/game_weeks_nba.svg';
import highestScoreUrl from '#src/components/card/Overlay/Stats/assets/stats/highest_score.svg';
import rewardsUrl from '#src/components/card/Overlay/Stats/assets/stats/rewards.svg';
import { getStatsInfo } from '#src/components/card/Overlay/Stats/drawHighlightedStat.ts';
import { getObtainedInfos } from '#src/components/card/Overlay/Stats/drawObtainedInfos.ts';
import { getImage } from '#src/components/card/Overlay/Stats/getImage.ts';
import { loadTypekitFonts } from '#src/lib/fonts.ts';
import { isType, withFragments } from '#src/lib/gql/index.ts';
import { splitName } from '#src/lib/players.ts';

import { Sport } from '#src/__generated__/globalTypes.ts';
import type { anyCardStatsOverlay2025_AnyCardInterface } from './__generated__/index.graphql.ts';

export const anyCardStatsOverlay2025 = withFragments(
  (card_: anyCardStatsOverlay2025_AnyCardInterface) => {
    const card = card_;

    return async (image: ImageBitmap) => {
      const canvas = document.createElement('canvas');
      const WIDTH = image.width;
      const HEIGHT = image.height;

      canvas.width = WIDTH;
      canvas.height = HEIGHT;

      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, WIDTH, HEIGHT);
      // draw texture
      ctx.drawImage(image, 0, 0);

      const promises: Promise<void>[] = [];

      // load font
      await loadTypekitFonts('yhr6iwl');

      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';

      // player name
      const [, lastName] = splitName(card.anyPlayer.displayName);
      ctx.font = `400 85.293px pressio`;
      ctx.fillText(lastName.toLocaleUpperCase(), WIDTH / 2, 480, 600);

      // jersey number
      (() => {
        ctx.save();

        const jerseyNumber = isType(card, 'Card')
          ? card.shirtNumber
          : card.anyPlayer.shirtNumber;
        if (jerseyNumber === null) {
          return;
        }
        ctx.strokeStyle = '#f00';

        const offsetTop = 530;
        const lineHeight = 170;
        const gradient = ctx.createLinearGradient(
          0,
          offsetTop,
          0,
          offsetTop + lineHeight
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0.6)');
        ctx.strokeStyle = gradient;

        ctx.lineWidth = 4.72;
        ctx.font = `400 220px pressio`;
        ctx.letterSpacing = '-4px';
        ctx.strokeText(
          jerseyNumber.toString(),
          WIDTH / 2,
          offsetTop + lineHeight
        );

        const textWidth = ctx.measureText(jerseyNumber.toString()).width;
        ctx.lineWidth = 2;
        ctx.font = `400 85px pressio`;
        ctx.textAlign = 'right';
        ctx.strokeText('#', WIDTH / 2 - textWidth / 2, offsetTop + 85);
        ctx.restore();
      })();

      promises.push(
        (async () => {
          if (card.tokenOwner?.user) {
            const { icon, label } = getObtainedInfos(
              card.tokenOwner.transferType
            );
            const obtainedIcon = await getImage(icon, false);
            ctx.save();
            ctx.textBaseline = 'top';
            ctx.translate(WIDTH / 2, 123);

            ctx.fillStyle = '#fff';
            ctx.strokeStyle = 'transparent';

            ctx.save();
            ctx.globalAlpha = 0.2;
            const iconSize = 130;
            ctx.drawImage(obtainedIcon, iconSize / -2, 0, iconSize, iconSize);
            ctx.restore();

            ctx.textAlign = 'center';
            ctx.font = `300 35px pressio`;
            ctx.fillText(label, 0, 30);

            const nickname = card.tokenOwner.user.nickname.slice(0, 16);

            ctx.font = `400 71px pressio`;
            if (nickname.length > 11) {
              ctx.font = `400 51px pressio`;
            }
            ctx.textBaseline = 'middle';
            ctx.fillText(card.tokenOwner.user.nickname, 0, 120);

            if (card.tokenOwner.from) {
              ctx.font = `300 35px pressio`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';
              ctx.fillText(
                new Date(card.tokenOwner.from)
                  .toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })
                  .toLocaleUpperCase(),
                0,
                170
              );
            }
            ctx.restore();
          }
        })()
      );

      const drawStat = async (
        iconUrl: string,
        x: number,
        y: number,
        label: string,
        value: number
      ) => {
        const icon = await getImage(iconUrl, false);
        ctx.save();

        ctx.translate(x, y);

        ctx.save();
        ctx.globalCompositeOperation = 'overlay';
        ctx.globalAlpha = 0.6;
        ctx.drawImage(icon, 13, 6, 62, 62);
        ctx.restore();

        ctx.font = `400 60px pressio-condensed`;
        ctx.fillText(value.toString() ?? '', 71, 45);

        ctx.font = `400 19px pressio-condensed`;
        ctx.fillText(label.toLocaleUpperCase(), 71, 75);

        ctx.restore();
      };

      //  Game weeks
      promises.push(
        drawStat(
          {
            [Sport.FOOTBALL]: gameWeeksUrl,
            [Sport.NBA]: gameWeeksNBAUrl,
            [Sport.BASEBALL]: gameWeeksMLBUrl,
          }[card.sport],
          147,
          977,
          'GAME WEEKS',
          card.cardStats?.so5LineupsCount ?? 0
        )
      );

      // Highest score
      const bestScore = Math.floor(
        card.cardStats?.bestSo5Appearance?.score ?? 0
      );
      promises.push(
        drawStat(highestScoreUrl, 315, 977, 'HIGHEST SCORE', bestScore)
      );

      // Rewards
      const so5RewardsCount = card.cardStats?.so5RewardsCount ?? 0;
      promises.push(drawStat(rewardsUrl, 483, 977, 'REWARDS', so5RewardsCount));
      if (card.cardStats) {
        // highlighted stat
        promises.push(
          (async () => {
            const highlightedStat = card.cardStats?.highlightedStats[0];
            if (!highlightedStat) {
              return;
            }

            const { icon: iconUrl, label } = getStatsInfo(highlightedStat.type);
            const icon = await getImage(iconUrl, false);

            const { value } = highlightedStat;

            ctx.save();
            ctx.translate(WIDTH / 2, 772);

            ctx.save();
            ctx.globalCompositeOperation = 'overlay';
            ctx.globalAlpha = 0.3;
            ctx.drawImage(icon, -80, 0, 161, 161);
            ctx.restore();

            ctx.textAlign = 'center';
            ctx.font = `500 71px pressio-condensed`;
            ctx.fillText(value.toString(), 0, 100);

            ctx.font = `400 35px pressio-condensed`;
            const titleWidth = ctx.measureText(label).width;

            const scale = Math.min(272 / titleWidth, 1);

            ctx.translate(0, 140);
            ctx.scale(scale, scale);
            ctx.fillText(label, 0, 0);
            ctx.restore();
          })()
        );
      }
      await Promise.all(promises);

      return createImageBitmap(canvas);
    };
  },
  {
    AnyCardInterface: gql`
      fragment anyCardStatsOverlay2025_AnyCardInterface on AnyCardInterface {
        slug
        sport
        ... on Card {
          slug
          shirtNumber
        }
        anyPlayer {
          slug
          displayName
          shirtNumber
        }
        cardStats {
          id
          so5LineupsCount
          so5RewardsCount
          bestSo5Appearance {
            id
            score
          }
          highlightedStats {
            type
            name
            value
          }
        }
        tokenOwner {
          id
          from
          transferType
          user {
            slug
            nickname
          }
        }
      }
    ` as TypedDocumentNode<anyCardStatsOverlay2025_AnyCardInterface>,
  }
);
